import React from "react";
import { CalendarDays, Star } from "lucide-react";
import * as api from "@/lib/api";
import { useData } from "@/hooks/useData";
import { Loader, PageHeader, SectionTitle, StatCard } from "@/components/ui";
import { cn } from "@/lib/utils";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function HolidayCalendar() {
  const { data: holidays } = useData(() => api.holidays.list(), []);
  if (!holidays) return <Loader />;

  const todayIso = new Date().toISOString().slice(0, 10);
  const upcoming = holidays.filter((h) => h.date >= todayIso);
  const byMonth = holidays.reduce((acc, h) => {
    const m = Number(h.date.slice(5, 7)) - 1;
    (acc[m] = acc[m] || []).push(h);
    return acc;
  }, {});

  return (
    <>
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total Holidays" value={holidays.length} icon={CalendarDays} />
        <StatCard label="Remaining" value={upcoming.length} icon={CalendarDays} color="green" />
        <StatCard label="Public" value={holidays.filter((h) => h.type === "Public").length} icon={Star} color="orange" />
        <StatCard label="Islamic" value={holidays.filter((h) => h.type === "Islamic").length} icon={Star} />
      </div>

      <PageHeader description="Official UAE public and Islamic holidays. Islamic dates are subject to moon sighting." />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {MONTHS.map((name, i) => {
          const list = byMonth[i] || [];
          return (
            <div key={name} className={cn("neu-card p-4", !list.length && "opacity-60")}>
              <div className="flex items-center justify-between mb-3">
                <SectionTitle>{name}</SectionTitle>
                <span className="text-[10px] text-slate-400 font-semibold">{list.length ? `${list.length} day${list.length > 1 ? "s" : ""}` : "—"}</span>
              </div>
              {list.length ? (
                <ul className="space-y-1">
                  {list.map((h) => (
                    <li key={h.date} className={cn("flex items-center gap-2.5 p-2 rounded-2xl transition-all hover:shadow-neu-xs", h.date < todayIso && "opacity-50")}>
                      <span className="h-9 w-9 shrink-0 rounded-xl shadow-neu-inset-xs flex items-center justify-center text-sm font-extrabold text-brand-orange">{Number(h.date.slice(8))}</span>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-brand-blue truncate">{h.name}</p>
                        <p className="text-[11px] text-slate-500">{h.type}</p>
                      </div>
                      {h.type === "Public" && <Star size={12} className="text-brand-orange fill-current shrink-0" />}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-[11px] text-slate-400 pl-3">No public holidays</p>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
